import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { UserMention } from '@/components/UserMention';
import { MessageSquare } from 'lucide-react';

interface TicketRow {
  id: string;
  guildId: string;
  userId: string;
  channelId: string;
  status: 'OPEN' | 'CLAIMED' | 'CLOSED';
  subject: string | null;
  claimedBy: string | null;
  createdAt: string;
  closedAt: string | null;
  messageCount: number;
}

type Filter = 'OPEN' | 'CLOSED' | 'ALL';

export function TicketList() {
  const { guildId } = useParams<{ guildId: string }>();
  const [filter, setFilter] = useState<Filter>('OPEN');

  const { data, isLoading, error } = useQuery({
    queryKey: ['tickets', guildId, filter],
    queryFn: () =>
      api<{ tickets: TicketRow[] }>(`/api/guilds/${guildId}/tickets?status=${filter}`),
    enabled: !!guildId,
  });

  const tickets = data?.tickets ?? [];

  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">Tickets — Server {guildId}</h1>
        <div className="flex gap-1">
          {(['OPEN', 'CLOSED', 'ALL'] as const).map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? 'default' : 'outline'}
              onClick={() => setFilter(f)}
            >
              {f === 'OPEN' ? 'Offen' : f === 'CLOSED' ? 'Geschlossen' : 'Alle'}
            </Button>
          ))}
        </div>
      </div>

      {isLoading && <p className="text-muted-foreground">Lade Tickets…</p>}
      {error && <p className="text-destructive">Fehler: {(error as Error).message}</p>}

      {!isLoading && tickets.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Keine Tickets</CardTitle>
            <CardDescription>
              Filter „{filterLabel(filter)}" liefert keine Ergebnisse.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      <div className="space-y-2">
        {tickets.map((t) => (
          <Link key={t.id} to={`/tickets/${t.id}`} className="block">
            <Card className="transition-colors hover:border-primary">
              <CardContent className="flex items-center gap-3 pt-6">
                <MessageSquare className="h-5 w-5 text-primary shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium truncate">
                      {t.subject || 'Ticket ohne Titel'}
                    </span>
                    <StatusBadge status={t.status} />
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    von <UserMention id={t.userId} /> · Channel <code>#{t.channelId}</code> ·{' '}
                    {new Date(t.createdAt).toLocaleString('de-DE')} · {t.messageCount} Nachrichten
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: 'OPEN' | 'CLAIMED' | 'CLOSED' }) {
  const styles: Record<string, string> = {
    OPEN: 'bg-primary/20 text-primary',
    CLAIMED: 'bg-yellow-500/20 text-yellow-500',
    CLOSED: 'bg-muted text-muted-foreground',
  };
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs ${styles[status]}`}>{status}</span>
  );
}

function filterLabel(f: Filter) {
  return f === 'OPEN' ? 'Offen' : f === 'CLOSED' ? 'Geschlossen' : 'Alle';
}
